import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Gift, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, startCheckout, useAuth } from "../lib/api";

export function TipButton({ recipient, comicId, className = "" }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [amt, setAmt] = useState(300);
  const [custom, setCustom] = useState("");
  const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/config/public").then((r) => setCfg(r.data)); }, []);
  if (!recipient || user?.id === recipient.id) return null;
  const cents = custom ? Math.round(parseFloat(custom) * 100) || 0 : amt;
  const fee = cfg ? Math.round(cents * cfg.processing_pct / 100 + cfg.processing_fixed_cents) + Math.floor(cents * cfg.tip_platform_pct / 100) : 0;
  const go = async () => {
    if (!user) return (window.location.href = "/login");
    setBusy(true);
    try { await startCheckout({ kind: "tip", recipient_id: recipient.id, amount_cents: cents, message: msg, comic_id: comicId }); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog onOpenChange={() => setConfirm(false)}>
      <DialogTrigger asChild><button data-testid="tip-btn" className={`jx-btn px-5 py-2.5 text-sm flex items-center gap-2 ${className}`}><Gift size={16} />Tip</button></DialogTrigger>
      <DialogContent className="jx-surface">
        <DialogHeader><DialogTitle className="font-display">Tip @{recipient.username}</DialogTitle></DialogHeader>
        {!confirm ? (<div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {cfg?.tip_presets_cents.map((p) => <button key={p} data-testid={`tip-preset-${p}`} onClick={() => { setAmt(p); setCustom(""); }} className={`py-3 rounded-xl border-2 font-bold ${!custom && amt === p ? "border-[var(--jx-pink)] jx-pink" : "border-[var(--jx-border)]"}`}>{money(p)}</button>)}
          </div>
          <input data-testid="tip-custom-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount (USD)" inputMode="decimal" className="w-full bg-transparent border rounded-xl px-4 py-3" />
          <input data-testid="tip-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={200} placeholder="Add a message (optional)" className="w-full bg-transparent border rounded-xl px-4 py-3" />
          <button data-testid="tip-continue-btn" disabled={cents < 100} onClick={() => setConfirm(true)} className="jx-btn w-full py-3">Continue</button>
        </div>) : (<div className="space-y-3" data-testid="tip-confirmation">
          <div className="flex justify-between"><span className="jx-muted">Tip amount</span><b>{money(cents)}</b></div>
          <div className="flex justify-between text-sm"><span className="jx-muted">Processing + platform fees (deducted from creator)</span><span>−{money(fee)}</span></div>
          <div className="flex justify-between text-sm"><span className="jx-muted">Creator receives approx.</span><span className="jx-mint font-bold">{money(Math.max(0, cents - fee))}</span></div>
          <p className="text-xs jx-muted">You'll be charged {money(cents)} via secure checkout. Tips are non-refundable except in cases of fraud.</p>
          <button data-testid="tip-confirm-btn" disabled={busy} onClick={go} className="jx-btn w-full py-3">{busy ? "Redirecting…" : `Confirm & pay ${money(cents)}`}</button>
        </div>)}
      </DialogContent>
    </Dialog>
  );
}

export function ReportButton({ type, id }) {
  const [reason, setReason] = useState("Spam");
  const [details, setDetails] = useState("");
  const [open, setOpen] = useState(false);
  const send = async () => {
    try { await api.post("/reports", { target_type: type, target_id: id, reason, details, is_copyright: reason === "Copyright infringement" }); toast.success("Report sent to moderators"); setOpen(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid={`report-${type}-btn`} className="jx-muted hover:text-[var(--jx-pink)] text-xs flex items-center gap-1"><Flag size={12} />Report</button></DialogTrigger>
      <DialogContent className="jx-surface">
        <DialogHeader><DialogTitle className="font-display">Report {type}</DialogTitle></DialogHeader>
        <select data-testid="report-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-transparent border rounded-xl px-3 py-3">
          {["Spam", "Harassment", "Inappropriate content", "Copyright infringement", "Fraud", "Other"].map((r) => <option key={r}>{r}</option>)}
        </select>
        <textarea data-testid="report-details-input" value={details} onChange={(e) => setDetails(e.target.value)} placeholder={reason === "Copyright infringement" ? "Describe the original work you own and where it appears (takedown request)" : "Details"} className="bg-transparent border rounded-xl px-3 py-3 h-28" />
        <button data-testid="report-submit-btn" onClick={send} className="jx-btn py-3">Submit report</button>
      </DialogContent>
    </Dialog>
  );
}
